// src/components/IrrigationList.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import ExcelReader from '../services/ExcelReader';

const IrrigationList = () => {
  const { t, language } = useLanguage();
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';
  const [activeTab, setActiveTab] = useState('valves');
  const [expandedValve, setExpandedValve] = useState(null);
  const [valves, setValves] = useState([]);
  const [soilRows, setSoilRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const dataSources = [
    { type: 'valves', fileName: 'VanaListesi.xlsx' },
    { type: 'soil', fileName: 'LokasyonBazliToprakVerileri.xlsx' }
  ];

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      setIsLoading(true);

      const [valveResult, soilResult] = await Promise.all([
        ExcelReader.readIrrigationValveList('VanaListesi.xlsx', 'Sulama'),
        ExcelReader.readIrrigationSoilByLocation('LokasyonBazliToprakVerileri.xlsx', 'Sulama')
      ]);

      if (!isMounted) {
        return;
      }

      setValves(valveResult.success ? valveResult.data : []);
      setSoilRows(soilResult.success ? soilResult.data : []);
      setIsLoading(false);
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, []);

  const soilByLocation = useMemo(() => {
    const map = new Map();
    soilRows.forEach((row) => {
      if (row.location) {
        map.set(row.location, row);
      }
    });
    return map;
  }, [soilRows]);

  const formatDateTime = (date) => {
    if (!date) {
      return '-';
    }
    return date.toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC'
    });
  };

  const formatValue = (value, suffix = '') => {
    if (value === null || value === undefined || value === '' || Number.isNaN(value)) {
      return '-';
    }
    return `${value}${suffix}`;
  };

  const statusLabel = (status) => {
    if (status === 'attention') {
      return 'Uyarı';
    }
    if (status === 'normal') {
      return 'Normal';
    }
    return status || '-';
  };

  return (
    <div className="space-y-6">
      <header className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">{t('lists.irrigation.title')}</h2>
            <p className="text-slate-700">{t('lists.irrigation.subtitle')}</p>
          </div>
          <div className="text-sm text-slate-500">
            {t('lists.totalFiles', { count: dataSources.length.toLocaleString(locale) })}
          </div>
        </div>
      </header>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-wrap gap-2 border-b border-slate-200 p-4">
          <button
            type="button"
            onClick={() => setActiveTab('valves')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'valves'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t('lists.irrigation.tabs.valves')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('soil')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'soil'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {t('lists.irrigation.tabs.soil')}
          </button>
        </div>

        {activeTab === 'valves' ? (
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t('lists.irrigation.table.valves.valve')}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t('lists.irrigation.table.valves.location')}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t('lists.irrigation.table.valves.status')}
                  </th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">
                    {t('lists.irrigation.table.valves.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {valves.map((valve) => {
                  const isExpanded = expandedValve === valve.id;
                  const soil = soilByLocation.get(valve.location);

                  return (
                    <React.Fragment key={valve.id}>
                      <tr className={`hover:bg-slate-50 ${isExpanded ? 'bg-slate-50' : ''}`}>
                        <td className="px-4 py-3 font-semibold text-slate-900">{valve.name}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                            {valve.location}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                            {statusLabel(valve.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedValve(isExpanded ? null : valve.id)
                            }
                            className="px-3 py-1 rounded-lg text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-500"
                          >
                            {t('lists.irrigation.actions.soilData')}
                          </button>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr>
                          <td colSpan="4" className="px-4 py-4 bg-slate-50">
                            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm space-y-4">
                              <div className="flex flex-col gap-1">
                                <h4 className="text-sm font-bold text-slate-900">
                                  {valve.location} için toprak özeti
                                </h4>
                                <p className="text-xs text-slate-600">
                                  Son güncelleme: {formatDateTime(soil?.lastIrrigation)}
                                </p>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <div className="text-xs text-slate-600">Nem</div>
                                  <div className="text-lg font-bold text-slate-900">
                                    {formatValue(soil?.soilMoisture, '%')}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-slate-600">Toprak Sıcaklığı</div>
                                  <div className="text-lg font-bold text-slate-900">
                                    {formatValue(soil?.soilTemperature, '°C')}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-slate-600">Durum</div>
                                  <div className="text-lg font-bold text-slate-900">
                                    {statusLabel(soil?.status)}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-xs text-slate-600">Son Sulama</div>
                                  <div className="text-lg font-bold text-slate-900">
                                    {formatDateTime(soil?.lastIrrigation)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            {soilRows.length === 0 && !isLoading ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center shadow-sm">
                <p className="text-lg font-semibold text-slate-900">
                  {t('lists.irrigation.empty.title')}
                </p>
                <p className="mt-2 text-sm text-slate-600">
                  {t('lists.irrigation.empty.description')}
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">
                        {t('lists.irrigation.table.soil.location')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">
                        {t('lists.irrigation.table.soil.soilTemperature')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">
                        {t('lists.irrigation.table.soil.soilMoisture')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">
                        {t('lists.irrigation.table.soil.lastIrrigation')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-700">
                        {t('lists.irrigation.table.soil.status')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {soilRows.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-900 font-medium">{row.location}</td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatValue(row.soilTemperature, '°C')}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatValue(row.soilMoisture, '%')}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {formatDateTime(row.lastIrrigation)}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{statusLabel(row.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default IrrigationList;
